import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useAuth } from '../../contexts/AuthContext';
import ReelItem from './components/ReelItem';
import ReelCreator from './components/ReelCreator';
import { idbStorage } from '../../lib/idbStorage';

// ─── Mock reels shown when Convex is offline / empty ─────────────────────────
const MOCK_REELS = [
  {
    id: 'mock-1',
    userId: 'system1',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1533750349088-cd871a92f312?w=400&fit=crop',
    caption: 'Grand Theft Auto VI Trailer 1. Welcome to Leonida. 🌴🚓 #RockstarGames',
    audioName: 'Love Is A Long Road — Tom Petty',
    duration: 90,
    viewsCount: 204520000,
    createdAt: Date.now() - 3600000,
    likesCount: 11240000,
    commentsCount: 892000,
    likes: [],
    comments: [],
    author: {
      id: 'system1',
      fullName: 'Rockstar Games',
      username: 'rockstargames',
      avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
    },
  },
  {
    id: 'mock-2',
    userId: 'system2',
    videoUrl: 'https://www.w3schools.com/html/movie.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1503614472-8c93d56e92ce?w=400&fit=crop',
    caption: 'Black Ops 6 - Cinematic Thriller Trailer. The truth lies. 🎯🚁 #CallOfDuty',
    audioName: 'Tension — Cinematic Score',
    duration: 120,
    viewsCount: 8900000,
    createdAt: Date.now() - 7200000,
    likesCount: 2210300,
    commentsCount: 148800,
    likes: [],
    comments: [],
    author: {
      id: 'system2',
      fullName: 'Call of Duty',
      username: 'callofduty',
      avatarUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100',
    },
  },
  {
    id: 'mock-3',
    userId: 'system3',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1446776811953-b23d5732162e?w=400&fit=crop',
    caption: 'Space Technology Breakthrough: First successful test of the new plasma propulsion thrusters. 🚀🌌',
    audioName: 'Orbital — Deep Space',
    duration: 9,
    viewsCount: 155000,
    createdAt: Date.now() - 86400000,
    likesCount: 45972,
    commentsCount: 2041,
    likes: [],
    comments: [],
    author: {
      id: 'system3',
      fullName: 'AeroSpace Daily',
      username: 'aero_space',
      avatarUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
    },
  },
  {
    id: 'mock-4',
    userId: 'system4',
    videoUrl: 'https://www.w3schools.com/html/movie.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&fit=crop',
    caption: 'Modern Architecture: How parametric design is reshaping the skyline of Neo-Tokyo. 🏢📐',
    audioName: 'Structural — Ambient',
    duration: 12,
    viewsCount: 34000,
    createdAt: Date.now() - 172800000,
    likesCount: 8900,
    commentsCount: 430,
    likes: [],
    comments: [],
    author: {
      id: 'system4',
      fullName: 'Architectural Digest',
      username: 'arch_digest',
      avatarUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
    },
  },
  {
    id: 'mock-5',
    userId: 'system5',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&fit=crop',
    caption: 'Technology Review: The new quantum core processors that are powering AI development. 💻⚡',
    audioName: 'Data Stream — Tech Beat',
    duration: 8,
    viewsCount: 78000,
    createdAt: Date.now() - 259200000,
    likesCount: 15600,
    commentsCount: 920,
    likes: [],
    comments: [],
    author: {
      id: 'system5',
      fullName: 'Tech Insider',
      username: 'tech_insider',
      avatarUrl: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
    },
  },
  {
    id: 'mock-6',
    userId: 'system6',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=400&fit=crop',
    caption: 'Unreal Engine 5 VFX Animation Movie. The line between game and cinema has officially vanished. 🎥🔥 #UnrealEngine #VFX',
    audioName: 'Cinematic Epic — Hans Zimmer Style',
    duration: 180,
    viewsCount: 3450000,
    createdAt: Date.now() - 43200000,
    likesCount: 890000,
    commentsCount: 12400,
    likes: [],
    comments: [],
    author: {
      id: 'system6',
      fullName: 'Epic VFX Studio',
      username: 'unreal_vfx',
      avatarUrl: 'https://images.unsplash.com/photo-1531297122539-5692f6e13b24?w=100',
    },
  },
  {
    id: 'mock-7',
    userId: 'system7',
    videoUrl: 'https://www.w3schools.com/html/movie.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400&fit=crop',
    caption: 'Video AI on Game Feature: Real-time generative AI rendering directly over gameplay. The future is here. 🤖🕹️ #GameDev',
    audioName: 'Generative Pulse — AI Generated Audio',
    duration: 45,
    viewsCount: 1280000,
    createdAt: Date.now() - 1200000,
    likesCount: 345000,
    commentsCount: 8900,
    likes: [],
    comments: [],
    author: {
      id: 'system7',
      fullName: 'AI Gaming Labs',
      username: 'ai_gamelabs',
      avatarUrl: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=100',
    },
  },
  {
    id: 'mock-8',
    userId: 'system8',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&fit=crop',
    caption: 'Cybernetic Horizons: Next-Gen RPG gameplay reveal. Over 1000 dynamically generated planets to explore. 🚀🎮',
    audioName: 'Neon Nights — Synth Droid',
    duration: 140,
    viewsCount: 5200000,
    createdAt: Date.now() - 3600000,
    likesCount: 1450000,
    commentsCount: 32000,
    likes: [],
    comments: [],
    author: {
      id: 'system8',
      fullName: 'Horizon Studios',
      username: 'horizon_rpg',
      avatarUrl: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100',
    },
  },
  {
    id: 'mock-9',
    userId: 'system9',
    videoUrl: 'https://www.w3schools.com/html/movie.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=400&fit=crop',
    caption: 'AI Technology: First look at the new Neural Engine framework running real-time path tracing on a mobile chip. 🤯📱',
    audioName: 'Silicon Pulse — Tech Beat',
    duration: 60,
    viewsCount: 2100000,
    createdAt: Date.now() - 7200000,
    likesCount: 650000,
    commentsCount: 8900,
    likes: [],
    comments: [],
    author: {
      id: 'system9',
      fullName: 'Silicon Future',
      username: 'silicon_ai',
      avatarUrl: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100',
    },
  },
  {
    id: 'mock-10',
    userId: 'system10',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=400&fit=crop',
    caption: 'Esports meets AI: Training an neural net to beat the top 10 ranked players in tactical shooters. The results are terrifying. 🤖🎯',
    audioName: 'Tactical Overdrive — Aggressive Synth',
    duration: 110,
    viewsCount: 8900000,
    createdAt: Date.now() - 86400000,
    likesCount: 2200000,
    commentsCount: 145000,
    likes: [],
    comments: [],
    author: {
      id: 'system10',
      fullName: 'Neural Esports',
      username: 'neural_esports',
      avatarUrl: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
    },
  },
  {
    id: 'mock-11',
    userId: 'system11',
    videoUrl: 'https://www.w3schools.com/html/mov_bbb.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1542423377-50cb0eecc447?w=400&fit=crop',
    caption: 'Fantasy RPG: 8K textures and real-time raytraced global illumination bringing medieval worlds to life. ⚔️🐉',
    audioName: 'Fantasy Tavern — Orchestral',
    duration: 135,
    viewsCount: 3800000,
    createdAt: Date.now() - 4800000,
    likesCount: 950000,
    commentsCount: 15400,
    likes: [],
    comments: [],
    author: {
      id: 'system11',
      fullName: 'RPG Central',
      username: 'rpg_central',
      avatarUrl: 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=100',
    },
  }
];

// ─── ReelsPage ────────────────────────────────────────────────────────────────
const ReelsPage = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [activeIndex, setActiveIndex] = useState(0);
  const [showCreator, setShowCreator] = useState(false);
  const containerRef = useRef(null);
  const observerRef = useRef(null);

  const currentUser = authUser
    ? {
        id: authUser.id,
        name: authUser.user_metadata?.full_name || authUser.email,
        username: authUser.user_metadata?.username || authUser.email?.split('@')[0],
        avatar: authUser.user_metadata?.avatar_url,
      }
    : null;

  // ── Convex hooks ────────────────────────────────────────────────────────────
  let convexReels = undefined;
  let likeMut = null;
  let commentMut = null;
  let createReelMut = null;
  let generateUploadUrlMut = null;
  let incrementViewsMut = null;

  try {
    convexReels = useQuery(api.reels.getReels);
    likeMut = useMutation(api.reels.likeReel);
    commentMut = useMutation(api.reels.addReelComment);
    createReelMut = useMutation(api.reels.createReel);
    generateUploadUrlMut = useMutation(api.reels.generateUploadUrl);
    incrementViewsMut = useMutation(api.reels.incrementViews);
  } catch (e) {
    // offline — use mocks
  }

  const [liveReels, setLiveReels] = useState(null);

  useEffect(() => {
    const fetchRedditReels = async () => {
      try {
        const subreddits = ['gaming', 'GamePhysics', 'Valorant', 'Overwatch', 'RocketLeague', 'GTA', 'Minecraft'];
        const fetchPromises = subreddits.map(sub => 
          fetch(`https://www.reddit.com/r/${sub}/hot.json?limit=15`)
            .then(res => res.json())
            .then(data => data.data.children.map(p => ({ ...p, subreddit: sub })))
            .catch(() => [])
        );
        const results = await Promise.all(fetchPromises);
        const posts = results.flat();
        
        // Shuffle posts for variety
        const shuffledPosts = posts.sort(() => 0.5 - Math.random());

        const videoPosts = shuffledPosts
          .filter(p => p.data && p.data.secure_media && p.data.secure_media.reddit_video)
          .map((p, idx) => ({
            id: `reddit-${p.data.id}`,
            userId: p.data.author,
            videoUrl: p.data.secure_media.reddit_video.fallback_url,
            thumbnailUrl: p.data.thumbnail && p.data.thumbnail.startsWith('http') ? p.data.thumbnail : 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&fit=crop',
            caption: p.data.title,
            audioName: `Original Audio — r/${p.subreddit}`,
            duration: p.data.secure_media.reddit_video.duration || 15,
            viewsCount: p.data.score * 120,
            createdAt: p.data.created_utc * 1000,
            likesCount: p.data.score,
            commentsCount: p.data.num_comments,
            likes: [],
            comments: [],
            author: {
              id: p.data.author,
              fullName: p.data.author,
              username: p.data.author,
              avatarUrl: 'https://images.unsplash.com/photo-1511512578047-dfb367046420?w=100&fit=crop',
            },
          }));
        if (videoPosts.length > 0) {
          setLiveReels(videoPosts);
        }
      } catch (err) {
        console.warn('Failed to fetch reddit reels', err);
      }
    };
    fetchRedditReels();
  }, []);

  const reels = [];
  if (convexReels && convexReels.length > 0) {
    reels.push(...convexReels);
  }
  if (liveReels && liveReels.length > 0) {
    reels.push(...liveReels);
  }
  if (reels.length === 0) {
    reels.push(...MOCK_REELS);
  }

  // ── IntersectionObserver for active reel detection ──────────────────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.index);
            setActiveIndex(idx);
            // Increment view count
            if (reels[idx] && !reels[idx].id?.startsWith('mock')) {
              incrementViewsMut?.({ reelId: reels[idx].id }).catch(() => {});
            }
          }
        });
      },
      { root: container, threshold: 0.65 }
    );

    const items = container.querySelectorAll('[data-reel-item]');
    items.forEach((el) => observerRef.current.observe(el));

    return () => observerRef.current?.disconnect();
  }, [reels, incrementViewsMut]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleLike = useCallback(
    async (reelId) => {
      if (!currentUser || !likeMut) return;
      try {
        await likeMut({ reelId, userId: currentUser.id });
      } catch (e) {
        console.warn('Like error:', e);
      }
    },
    [currentUser, likeMut]
  );

  const handleAddComment = useCallback(
    async (reelId, content) => {
      if (!currentUser || !commentMut) return;
      try {
        await commentMut({ reelId, userId: currentUser.id, content });
      } catch (e) {
        console.warn('Comment error:', e);
      }
    },
    [currentUser, commentMut]
  );

  const handleShare = useCallback((reel) => {
    if (navigator.share) {
      navigator.share({ title: reel.caption || 'Check this reel', url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
    }
  }, []);

  const handlePublish = useCallback(
    async ({ videoFile, videoPreview, caption, audioName }) => {
      if (!currentUser) return;

      let videoUrl = videoPreview;

      // Upload to Convex storage if we have a real file and the mutation
      if (videoFile && generateUploadUrlMut && createReelMut) {
        try {
          const uploadUrl = await generateUploadUrlMut();
          const res = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': videoFile.type },
            body: videoFile,
          });
          const { storageId } = await res.json();
          videoUrl = storageId;
        } catch (e) {
          console.warn('Upload failed, attempting idbStorage:', e);
          videoUrl = await idbStorage.saveMedia(videoFile, 'video');
        }
      } else if (videoFile) {
        // Fallback for mock/offline mode
        videoUrl = await idbStorage.saveMedia(videoFile, 'video');
      }

      if (createReelMut) {
        await createReelMut({
          userId: currentUser.id,
          videoUrl,
          caption,
          audioName,
        });
      }
    },
    [currentUser, generateUploadUrlMut, createReelMut]
  );

  // ── Keyboard navigation ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        const next = Math.min(activeIndex + 1, reels.length - 1);
        containerRef.current
          ?.querySelectorAll('[data-reel-item]')
          ?.[next]?.scrollIntoView({ behavior: 'smooth' });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        const prev = Math.max(activeIndex - 1, 0);
        containerRef.current
          ?.querySelectorAll('[data-reel-item]')
          ?.[prev]?.scrollIntoView({ behavior: 'smooth' });
      } else if (e.key === 'Escape') {
        navigate('/');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeIndex, reels, navigate]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black z-[100]" style={{ overflow: 'hidden' }}>

      {/* ── Top bar ── */}
      <div
        className="absolute top-0 left-0 right-0 z-30 flex items-center justify-between px-4 pt-4 pb-2"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, transparent 100%)',
          pointerEvents: 'none',
        }}
      >
        <button
          onClick={() => navigate('/')}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', pointerEvents: 'all' }}
        >
          <span className="material-symbols-outlined text-white text-[22px]">arrow_back</span>
        </button>

        <div className="text-center" style={{ pointerEvents: 'none' }}>
          <p className="text-white font-bold tracking-widest text-sm uppercase">Reels</p>
          <p className="text-white/40 text-[10px]">{activeIndex + 1} / {reels.length}</p>
        </div>

        <button
          onClick={() => currentUser ? setShowCreator(true) : navigate('/login')}
          className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: 'linear-gradient(135deg,#c9a96e,#e8c98a)',
            pointerEvents: 'all',
          }}
        >
          <span className="material-symbols-outlined text-black text-[22px]">add</span>
        </button>
      </div>

      {/* ── Reel feed ── */}
      <div
        ref={containerRef}
        className="w-full h-full overflow-y-scroll"
        style={{
          scrollSnapType: 'y mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {reels.map((reel, idx) => (
          <div key={reel.id} data-reel-item data-index={idx}>
            <ReelItem
              reel={reel}
              isActive={activeIndex === idx}
              currentUser={currentUser}
              onLike={handleLike}
              onAddComment={handleAddComment}
              onShare={handleShare}
            />
          </div>
        ))}
      </div>

      {/* ── Dot navigation ── */}
      <div className="absolute right-1 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-1.5">
        {reels.map((_, idx) => (
          <button
            key={idx}
            onClick={() => {
              containerRef.current
                ?.querySelectorAll('[data-reel-item]')
                ?.[idx]?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="rounded-full transition-all duration-300"
            style={{
              width: activeIndex === idx ? '6px' : '4px',
              height: activeIndex === idx ? '20px' : '4px',
              background: activeIndex === idx
                ? 'linear-gradient(#c9a96e,#e8c98a)'
                : 'rgba(255,255,255,0.3)',
            }}
          />
        ))}
      </div>

      {/* ── Creator modal ── */}
      {showCreator && (
        <ReelCreator
          currentUser={currentUser}
          onClose={() => setShowCreator(false)}
          onPublish={handlePublish}
        />
      )}

      {/* CSS for audio ticker + hide scrollbar */}
      <style>{`
        @keyframes ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }
        [data-reel-item] + [data-reel-item] { /* ensure snap on all items */ }
        div::-webkit-scrollbar { display: none; }
      `}</style>
    </div>
  );
};

export default ReelsPage;
